import { NavLink, useLocation } from 'react-router';

import { paths } from '@/app/routes/paths';
import chevronLeftIcon from '@/assets/icons/chevron-left.svg';
import dashboardIcon from '@/assets/sidebar/dashboard.svg';
import participantHubIcon from '@/assets/sidebar/participant-hub.svg';
import incidentManagementIcon from '@/assets/sidebar/incident-management.svg';
import rosterLogisticsIcon from '@/assets/sidebar/roster-logistics.svg';
import trainingHubIcon from '@/assets/sidebar/training-hub.svg';
import workforceIcon from '@/assets/sidebar/workforce.svg';
import { useLocalStorageState } from '@/shared/hooks/useLocalStorageState';
import { messages } from '@/shared/i18n';
import { iconMaskStyle } from '@/shared/lib/icon-mask';

interface SidebarItem {
  id: string;
  label: string;
  icon: string;
  to: string;
}

const hubs = messages.workspace.hubs;
const ts = messages.sidebar;

const SIDEBAR_ITEMS: readonly SidebarItem[] = [
  { id: 'dashboard', label: hubs.dashboard.label, icon: dashboardIcon, to: paths.dashboard },
  { id: 'patients-hub', label: hubs.participantHub.label, icon: participantHubIcon, to: paths.participantHub },
  { id: 'training-hub', label: hubs.trainingHub.label, icon: trainingHubIcon, to: paths.trainingHub },
  { id: 'workforce', label: hubs.workforce.label, icon: workforceIcon, to: paths.workforce },
  { id: 'roster-logistics', label: hubs.rosterLogistics.label, icon: rosterLogisticsIcon, to: paths.rosterLogistics },
  { id: 'incident-management', label: hubs.incidentManagement.label, icon: incidentManagementIcon, to: paths.incidentManagement },
];

const SIDEBAR_COLLAPSED_STORAGE_KEY = 'salaamah.sidebarCollapsed';

export function DashboardSidebar() {
  const [collapsed, setCollapsed] = useLocalStorageState<boolean>(
    SIDEBAR_COLLAPSED_STORAGE_KEY,
    false,
  );
  const location = useLocation();
  // Treat any /onboarding/* route as a "dashboard" context so the dashboard
  // tab reads as active while the admin is still setting up the organisation.
  const isOnboarding = location.pathname.startsWith(paths.onboarding);
  const toggle = () => {
    setCollapsed((v) => !v);
  };

  return (
    <aside
      className={`relative z-50 flex hidden h-full min-h-0 shrink-0 flex-col overflow-visible border-r border-stroke bg-white transition-[width] duration-200 ease-out md:block ${
        collapsed ? 'w-[96px]' : 'w-[272px]'
      }`}
      aria-label={ts.navAriaLabel}
    >
      <button
        type="button"
        onClick={toggle}
        aria-label={collapsed ? ts.expand : ts.collapse}
        aria-expanded={!collapsed}
        className="absolute -right-3 top-6 z-[100] flex size-6 items-center justify-center rounded-full border border-stroke-strong bg-white text-ink-strong shadow-sm transition-colors hover:bg-brand-50 hover:text-brand"
      >
        <span
          aria-hidden
          className={`block size-3 transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`}
          style={iconMaskStyle(chevronLeftIcon)}
        />
      </button>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden p-6">
        <p
          className={`mb-3 text-tiny font-normal leading-none tracking-normal text-ink-strong ${
            collapsed ? 'text-center' : ''
          }`}
        >
          {ts.menuHeading}
        </p>

        <nav aria-label={ts.menuAriaLabel}>
          <ul className="flex flex-col gap-0.5">
            {SIDEBAR_ITEMS.map((item) => (
              <li key={item.id}>
                <NavLink
                  to={item.to}
                  // Child routes (e.g. `/patients-hub/new-intake`, `/training-hub/new`)
                  // should keep their parent hub highlighted — `end` only matches exact.
                  end={
                    item.id !== 'patients-hub' &&
                    item.id !== 'workforce' &&
                    item.id !== 'training-hub'
                  }
                  title={collapsed ? item.label : undefined}
                  className={({ isActive }) => {
                    const active = isActive || (item.id === 'dashboard' && isOnboarding);
                    return `flex w-full min-w-0 items-center rounded-lg px-3 py-2.5 text-[15px] font-medium leading-snug transition-colors ${
                      collapsed ? 'justify-center gap-0' : 'gap-2.5'
                    } ${
                      active
                        ? 'bg-brand text-white'
                        : 'text-ink-strong hover:bg-brand-50'
                    }`;
                  }}
                >
                  <span className="flex size-5 shrink-0 items-center justify-center p-[2px]">
                    <span
                      aria-hidden
                      className="block size-4"
                      style={iconMaskStyle(item.icon)}
                    />
                  </span>
                  {collapsed ? (
                    <span className="sr-only">{item.label}</span>
                  ) : (
                    <span className="min-w-0 flex-1">{item.label}</span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </aside>
  );
}
